/**
 * Custom Adventures Dashboard
 * 
 * Comprehensive dashboard for managing all aspects of custom adventures business:
 * - Creation and management of custom adventure offerings
 * - Review and response to customer booking requests
 * - Analytics and performance tracking (future)
 * 
 * This consolidates functionality previously split between activities and adventure-requests
 * to provide a better UX for business users managing their custom adventure business.
 * 
 * @author Wildlife Project Team
 * @version 2.0 - Updated with Business Design System
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Heart, 
  Settings, 
  BarChart3, 
  Users, 
  Calendar,
  DollarSign,
  TrendingUp,
  Star,
  MessageSquare,
  RefreshCw,
  Sparkles,
  Wand2,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  getCustomAdventures, 
  getBusinessBookingRequests, 
  type CustomAdventure,
  type BookingRequestStatistics 
} from '@/utils/businessAppApi';

// Import design system components and tokens
import { 
  colors, 
  shadows, 
  borders, 
  typography, 
  spacing,
  getCardClasses,
  getShadowClasses,
  getButtonClasses
} from '@/components/business/design-system/businessDesignTokens';

// Import existing components that we'll integrate
import { CustomAdventureRequestManagement } from '@/components/business/features/bookings/CustomAdventureRequestManagement';
import { AdventureManagementTab } from './components/AdventureManagementTab';

interface DashboardStats {
  totalAdventures: number;
  activeAdventures: number;
  totalRequests: number;
  pendingRequests: number;
  thisMonthRevenue: number;
  averageRating: number;
}

export function CustomAdventuresDashboard() {
  const [activeTab, setActiveTab] = useState('adventures');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalAdventures: 0,
    activeAdventures: 0,
    totalRequests: 0,
    pendingRequests: 0,
    thisMonthRevenue: 0,
    averageRating: 0
  });
  const [adventures, setAdventures] = useState<CustomAdventure[]>([]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      console.log('🔄 [Dashboard] Starting to load dashboard data...');
      
      // Load adventures
      const adventuresData = await getCustomAdventures();
      console.log('✅ [Dashboard] Adventures loaded:', adventuresData);
      setAdventures(adventuresData);

      // Load booking requests to get statistics
      const bookingData = await getBusinessBookingRequests();
      console.log('✅ [Dashboard] Booking data loaded:', bookingData);

      // Calculate stats
      const dashboardStats: DashboardStats = {
        totalAdventures: adventuresData.length,
        activeAdventures: adventuresData.filter(a => a.isActive && a.published).length,
        totalRequests: bookingData.requests.length,
        pendingRequests: bookingData.requests.filter(r => r.status === 'pending' || r.status === 'under_review').length,
        thisMonthRevenue: bookingData.statistics?.thisMonthRequests * 500 || 0, // Estimated
        averageRating: 4.8 // Mock data - would come from reviews
      };

      console.log('📊 [Dashboard] Stats calculated:', dashboardStats);
      setStats(dashboardStats);
    } catch (error) {
      console.error('❌ [Dashboard] Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (isActive: boolean, published: boolean) => {
    if (isActive && published) return 'bg-emerald-100 text-emerald-800 border-emerald-200/50';
    if (isActive && !published) return 'bg-amber-100 text-amber-800 border-amber-200/50';
    return 'bg-slate-100 text-slate-800 border-slate-200/50';
  };

  const getStatusText = (isActive: boolean, published: boolean) => {
    if (isActive && published) return 'Live';
    if (isActive && !published) return 'Draft';
    return 'Inactive';
  };

  if (loading) {
    return (
      <div className="space-y-6 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-96 bg-slate-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8">
      {/* Enhanced Header with Business Design System */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
              <Wand2 className="w-2.5 h-2.5 text-white" />
            </div>
          </div>
          <div>
            <h1 className={cn(typography.h1, "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent")}>
              Custom Adventures
            </h1>
            <p className="text-slate-600 mt-1 leading-relaxed">
              Create personalized adventure experiences with dynamic pricing and customer management
            </p>
          </div>
        </div>
        
      </motion.div>

      {/* Statistics Cards with Design System */}
      {/* Removed summary cards as per user request */}

      {/* Unified Tab System */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="adventures" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Adventures
          </TabsTrigger>
          <TabsTrigger value="requests" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            Booking Requests
          </TabsTrigger>
        </TabsList>

        <TabsContent value="adventures" className="space-y-6 mt-6">
          <AdventureManagementTab 
            adventures={adventures}
            loading={loading}
            onAdventuresUpdated={loadDashboardData}
          />
        </TabsContent>

        <TabsContent value="requests" className="space-y-6 mt-6">
          <CustomAdventureRequestManagement />
        </TabsContent>


      </Tabs>
    </div>
  );
} 